import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './MobBottomSheet.css';

interface MobBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  ariaLabel?: string;
  children: ReactNode;
  /**
   * Cap on sheet height in `vh`. Defaults to 80. Callers that need to keep
   * content visible above the sheet (e.g., a chart the sheet references)
   * should pass a smaller value so the sheet doesn't cover it.
   */
  maxHeightVh?: number;
}

/**
 * Reusable bottom sheet. Slides up from the bottom over a dimmed scrim.
 * Tap-outside to close (via scrim), Escape to close, safe-area padding for
 * iPhone home indicator, respects prefers-reduced-motion.
 */
export function MobBottomSheet({
  isOpen,
  onClose,
  ariaLabel,
  children,
  maxHeightVh = 80,
}: MobBottomSheetProps) {
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    // Move focus into the sheet for keyboard users.
    bodyRef.current?.focus();
    // Prevent the page behind from scrolling while the sheet is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Portal to <body> so `position: fixed` truly anchors to the viewport.
  // Any ancestor with a CSS `transform` (e.g. MobPullToRefresh) turns
  // `fixed` into `relative-to-ancestor`, which floats the sheet in the
  // middle of the page. Same trap MobFab avoids.
  return createPortal(
    <div
      className="mob-sheet-root"
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
    >
      <button
        type="button"
        aria-label="Close"
        className="mob-sheet-scrim"
        onClick={onClose}
      />
      <div
        ref={bodyRef}
        className="mob-sheet-body"
        tabIndex={-1}
        style={{ maxHeight: `${maxHeightVh}vh` }}
      >
        <div className="mob-sheet-grab" aria-hidden="true" />
        {children}
      </div>
    </div>,
    document.body,
  );
}
